// OttoBrief: Otto's system instruction as ordered, named sections of text.
// Code assembles the sections in order (skipping sections not meant for the
// consult's mode), then appends the facts block and the guest context. The
// fixed order matters: character leads (design 5.7, the Sept 14 lesson).
import { z } from "zod";
import { ConfigMode, DraftLabel, Id, behaviorText, requireUnique, templateText } from "./common";

export const BriefSection = z.object({
  id: Id,
  title: behaviorText(120),
  text: behaviorText(20_000),
  /** Omit to use the section in both modes. Video and voice only differ by about two lines (design 5.7). */
  modes: z.array(ConfigMode).min(1).optional(),
});
export type BriefSection = z.infer<typeof BriefSection>;

export const OttoBrief = z
  .object({
    draft_label: DraftLabel,
    sections: z.array(BriefSection).min(1).max(40),
    /**
     * The note code sends to make Otto open the call (design 3.4 step 4).
     * Placeholders: {first_name}, {business_name}.
     */
    opening_cue: templateText(1000),
    /**
     * Optional. The note that makes Otto open a consult the owner came back
     * to finish (Oct 18 2026). Absent: the opening cue is used. Placeholders:
     * {first_name}, {business_name}.
     */
    resume_cue: templateText(1000).optional(),
    /**
     * Descriptions of Otto's only two tools (design 4.1): the model reads
     * these, so they are behavior and live here, not in code.
     */
    tool_descriptions: z.object({
      end_consult: behaviorText(1000),
      flag_human_request: behaviorText(1000),
    }),
    /**
     * How notes are marked for Otto (design 4.1: a note is labeled so it is
     * never taken for the owner's words). Code writes "<label>: <note>" for
     * every note and cue; the brief tells Otto what each label means. Added
     * at integration, Sept 28 2026: the v0 brief already promised the labels
     * but nothing sent them.
     */
    note_labels: z
      .object({
        /** The coach's hints and the "what I know so far" ledger note. */
        coach: behaviorText(40).default("COACH NOTE"),
        /** Clock notes and every cue (the opening, silence nudges, the close). */
        clock: behaviorText(40).default("CLOCK NOTE"),
        /** The drafted read-back. */
        readback: behaviorText(40).default("READBACK DRAFT"),
        /**
         * The drafted mid-call checkpoint (Oct 14 2026). Before this it went
         * out under the read-back label, and Otto took it for the read-back
         * and wrapped up early (the 12-prospect rehearsal).
         */
        checkpoint: behaviorText(40).default("CHECKPOINT"),
      })
      .prefault({}),
  })
  .superRefine((brief, ctx) => {
    requireUnique(
      brief.sections.map((s) => s.id),
      ctx,
      ["sections"],
      "section id",
    );
  });
export type OttoBrief = z.infer<typeof OttoBrief>;

/** The sections for one mode, in order. */
export function briefSectionsFor(brief: OttoBrief, mode: ConfigMode): BriefSection[] {
  return brief.sections.filter((s) => !s.modes || s.modes.includes(mode));
}
